<template>
  <div class="voting-area">
    <div class="voting-box">
      <div class="text-area">
        <h1>
          {{ $t('voting.header') }}
        </h1>
        <p class="desc">
          {{ $t('voting.description') }}
        </p>
        <nuxt-link v-if="user !== null" :to="localePath('/voting/proposals/create')" class="btn cyan">
          {{ $t('voting.create_proposal') }}
        </nuxt-link>
        <button v-else class="btn cyan" @click="openConnectWallet">
          {{ $t('voting.connect_to_make') }}
        </button>
      </div>
      <div class="image-area">
        <div class="picture">
          <picture>
            <source srcset="/img/new/voting.webp" type="image/webp">
            <nuxt-img
              fit="inside"
              width="836"
              sizes="sm:300px md:400px lg:500px xl:500px xxl:600px"
              src="/img/new/voting.png"
              alt="Governance DAO voting using $SPLASHIE on Defi Pool Finance"
              title="$SPLASHIE governance"
            />
          </picture>
        </div>
      </div>
      <div class="cyan-cloud">
        <CloudCyanVotingRight />
      </div>
      <div class="cyan-cloud2">
        <CloudCyanVotingLeft />
      </div>
    </div>
    <div class="h2">
      {{ $t('voting.propossals') }}
    </div>
    <div class="filter-row">
      <div class="left">
        <div :class="{'filter-item': true, 'active': filter.type === 'core'}" @click.prevent="changeType('core')">
          <Verified />
          <span class="title">
            {{ $t('voting.core') }}
          </span>
        </div>
        <div :class="{'filter-item': true, 'active': filter.type === 'community'}" @click.prevent="changeType('community')">
          <Community />
          <span class="title">
            {{ $t('voting.community') }}
          </span>
        </div>
        <div :class="{'filter-item': true, 'active': filter.type === 'all'}" @click.prevent="changeType('all')">
          <List />
          <span class="title">
            {{ $t('voting.all') }}
          </span>
        </div>
      </div>
      <div class="right">
        <div class="radio-input">
          <input
            id="period_now"
            value="now"
            type="radio"
            name="period"
            :checked="filter.status === 'active'"
            @input="changePeriod('active')"
          >
          <label for="period_now">
            {{ $t('voting.vote_now') }}
          </label>
        </div>
        <div class="radio-input">
          <input
            id="period_soon"
            value="soon"
            type="radio"
            name="period"
            :checked="filter.status === 'soon'"
            @input="changePeriod('soon')"
          >
          <label for="period_soon">
            {{ $t('voting.soon') }}
          </label>
        </div>
        <div class="radio-input">
          <input
            id="period_closed"
            value="closed"
            type="radio"
            name="period"
            :checked="filter.status === 'closed'"
            @input="changePeriod('closed')"
          >
          <label for="period_closed">
            {{ $t('voting.closed') }}
          </label>
        </div>
      </div>
    </div>
    <div class="votings">
      <div v-if="proposals.length > 0 && !proposalsLoading" class="voting-table">
        <div v-for="(proposal, ind) in proposals" :key="ind" class="voting-row">
          <div class="voting-title">
            {{ proposal.title }}
          </div>
          <div class="ends">
            <i class="bx bx-calendar"></i>
            {{ $t('voting.ends_in') }}
            {{ $beautifyDate(proposal.expires) }}
          </div>
          <div class="statuses">
            <div v-if="proposal.type === 'core'" class="status core">
              <Verified />
              {{ $t('voting.core') }}
            </div>
            <div v-if="proposal.type === 'community'" class="status community">
              <Community />
              {{ $t('voting.community') }}
            </div>
          </div>
          <nuxt-link :to="localePath('/voting/proposals/' + proposal.snapshot)" class="btn cyan open">
            {{ $t('voting.vote') }}
          </nuxt-link>
        </div>
      </div>
      <div v-else-if="proposalsLoading" class="voting-table">
        <div v-for="ind in 5" :key="ind" class="voting-row">
          <Skeleton width="150px" height="22px" class="voting-title" />
          <Skeleton width="100px" height="14px" class="ends" />
          <div class="statuses">
            <Skeleton width="75px" height="18px" style="margin-right: 1rem" />
            <Skeleton width="75px" height="18px" />
          </div>
          <Skeleton width="50px" height="20px" class="open" />
        </div>
      </div>
      <div v-else class="voting-table empty-area">
        <div class="h4">
          {{ $t('voting.no_proposals') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import {
  hydrateWhenIdle
} from 'vue-lazy-hydration'
import { Skeleton } from 'vue-loading-skeleton'

export default {
  name: 'VotingPage',
  components: {
    CloudCyanVotingLeft: hydrateWhenIdle(() => import('~/components/svg/new/clouds/CloudCyanVotingLeft')),
    CloudCyanVotingRight: hydrateWhenIdle(() => import('~/components/svg/new/clouds/CloudCyanVotingRight')),
    Community: hydrateWhenIdle(() => import('~/components/svg/new/Community')),
    List: hydrateWhenIdle(() => import('~/components/svg/new/List')),
    Skeleton,
    Verified: hydrateWhenIdle(() => import('~/components/svg/new/Verified'))
  },
  layout: 'new',
  data() {
    return {
      filter: {
        type: 'core',
        status: 'active'
      }
    }
  },
  async fetch({ store, error, route }) {
    try {
      const pageName = route.name.split('___')
      const seoData = store.getters['seos/seosByUri'](pageName[0])
      if (seoData === null) {
        await store.dispatch('seos/fetchSeos', {
          uri: route.name
        })
      }
    } catch (err) {
      error(err)
    }
  },
  head({ $config: { appUrl } }) {
    return {
      title: this.meta ? this.meta.title : this.$t('common.launching'),
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.meta ? this.meta.description : ''
        },
        {
          hid: 'og-description',
          name: 'og:description',
          content: this.meta ? this.meta.description : ''
        },
        {
          hid: 'og-title',
          name: 'og:title',
          content: this.meta
            ? this.meta.title
            : this.$t('common.launching')
        },
        {
          hid: 'og-image',
          name: 'og:image',
          content: this.meta
            ? this.meta.image
            : appUrl + 'seo/default.jpg'
        },
        {
          hid: 'keywords',
          name: 'keywords',
          content: this.meta ? this.meta.keyword : ''
        }
      ]
    }
  },
  computed: {
    ...mapGetters('seos', [
      'seosByUri'
    ]),
    ...mapGetters('auth', [
      'user'
    ]),
    ...mapGetters('proposals', [
      'proposals',
      'total',
      'proposalsLoading'
    ]),
    meta() {
      const pageName = this.$nuxt.$route.name.split('___')
      return this.seosByUri(pageName[0])
    }
  },
  mounted() {
    this.fetchProposals(this.filter)
  },
  methods: {
    ...mapActions('proposals', [
      'fetchProposals'
    ]),
    ...mapActions('modal', [
      'changeModalWalletState'
    ]),
    changeType(newType) {
      if (this.filter.type.toLowerCase() !== newType.toLowerCase()) {
        this.filter.type = newType.toLowerCase()
        this.refetchProposals()
      }
    },
    changePeriod(newPeriod) {
      if (this.filter.status.toLowerCase() !== newPeriod.toLowerCase()) {
        this.filter.status = newPeriod.toLowerCase()
        this.refetchProposals()
      }
    },
    refetchProposals() {
      this.filter.page = 1
      this.fetchProposals(this.filter)
    },
    openConnectWallet() {
      this.changeModalWalletState({
        state: true
      })
    }
  }
}
</script>

<style scoped>
  @import url('~/assets/css/new/voting.css');
</style>
